import { Button, Grid, MenuItem, Select } from '@mui/material';
import { SecuredLayout } from '../Layout/SecuredLayout';
import React, { useState } from 'react';
import { Role } from '../../@types/auth';
import { useAuth, useProvideSnackBar } from '../../hooks';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import huLocale from 'date-fns/locale/hu';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { formatDateToYyyyMMdd } from '../../utils';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ERROR_MESSAGES, TigFormValues, tigSchema } from './tigExcelExport.schema';
import { downloadFile } from '../../utils/file-downloader';
import { usePartnerList } from '../../hooks/usePartnerList';
import Navbar from '../Layout/Navbar';

const PLEASE_SELECT = 'please_select';

export default function TigExcelExport() {
    const { user } = useAuth();
    const { showError, showResponseError } = useProvideSnackBar();
    const { fetchResource } = usePreconfiguredFetch();
    const [isDownloading, setIsDownloading] = useState(false);
    const { partnerList } = usePartnerList();
    const form = useForm<TigFormValues>({
        defaultValues: {
            partner: PLEASE_SELECT,
            from: (() => {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth() - 1, 1);
            })(),
            to: (() => {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), 0);
            })(),
        },
        resolver: zodResolver(tigSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const generateExcelExportUri = () => {
        const params = {
            partner: form.watch('partner'),
            from: formatDateToYyyyMMdd(form.watch('from') as Date),
            to: formatDateToYyyyMMdd(form.watch('to') as Date),
            t: user?.accessToken || '',
        };
        return '/api/tig/xlsx_export?' + new URLSearchParams(params).toString();
    };

    const onDownLoadExcel = async () => {
        const isValid = await form.trigger();
        if (!isValid) {
            showError(ERROR_MESSAGES.checkTheFields);
            return;
        }

        setIsDownloading(true);
        const response = await fetchResource(generateExcelExportUri());

        if (!response.ok) {
            await showResponseError(response);
            setIsDownloading(false);
            return;
        }

        const documentTitle = {
            partner: form.watch('partner'),
            from: formatDateToYyyyMMdd(form.watch('from') as Date),
            to: formatDateToYyyyMMdd(form.watch('to') as Date),
        };

        await downloadFile(response, documentTitle);
        setIsDownloading(false);
    };

    if (
        !(
            user?.roles.includes(Role.UGYINTEZO) ||
            user?.roles.includes(Role.VEZENYLO) ||
            user?.roles.includes(Role.FOVEZENYLO) ||
            user?.roles.includes(Role.ADMIN)
        )
    ) {
        return null;
    }

    return (
        <SecuredLayout>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={huLocale}>
                <Grid container p={2} spacing={2} sx={{ alignItems: 'end' }}>
                    <Grid container px={1} py={1} spacing={1}>
                        <Grid item xs={12} px={1.5} py={0.5} textAlign={'right'}>
                            <Navbar />
                        </Grid>
                    </Grid>
                    <Grid container display='flex' alignItems='center' justifyContent='flex-start' spacing={2} p={2}>
                        <Grid item>
                            <Controller
                                name='from'
                                control={form.control}
                                render={({ field, formState: { errors } }) => (
                                    <DateRangePicker
                                        label={'Időszak'}
                                        sx={{ width: '14rem' }}
                                        value={[form.watch('from'), form.watch('to')] as [Date | null, Date | null]}
                                        onChange={newValue => {
                                            const [from, to] = newValue;
                                            field.onChange(from);
                                            form.setValue('to', to);
                                            form.trigger('from');
                                            form.trigger('to');
                                        }}
                                        slots={{ field: SingleInputDateRangeField }}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                fullWidth: true,
                                                error: !!errors.from || !!errors.to,
                                                helperText: errors.from?.message || errors.to?.message || ' ',
                                            },
                                        }}
                                        calendars={2}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item>
                            <Controller
                                name='partner'
                                control={form.control}
                                render={({ field, formState: { errors } }) => (
                                    <Select
                                        variant='standard'
                                        labelId='partner-label'
                                        value={field.value}
                                        label='Partner'
                                        sx={{ minWidth: '200px' }}
                                        onChange={field.onChange}
                                        error={!!errors.partner}>
                                        <MenuItem value={PLEASE_SELECT}>Kérem válasszon!</MenuItem>
                                        {partnerList.map(p => (
                                            <MenuItem key={p.ID} value={p.Name}>
                                                {p.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                endIcon={<FileDownloadIcon />}
                                variant='contained'
                                onClick={onDownLoadExcel}
                                sx={{ borderRadius: '50px' }}
                                disabled={isDownloading}>
                                {isDownloading ? 'Letöltés folyamatban…' : 'Teljesítési igazolás letöltése'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </SecuredLayout>
    );
}
